import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  TextField,
  Tooltip,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import EventIcon from '@material-ui/icons/Event';
import MomentUtils from '@date-io/moment';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import moment from 'moment';
import 'moment/locale/pt-br';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { toastr } from 'react-redux-toastr';
import { formatPhone } from '../../../../../../helpers/utils';
import {
  agendar,
  buscarDisponibilidade,
  buscarMeusAgendamentos,
  cancelar,
} from './scheduleActions';
import {
  bloqueioDaEspecialidade,
  chaveDoDia,
  CONVENIOS,
  daEspecialidade,
  dataDaChave,
  ESPECIALIDADES,
  indexarDias,
  mensagemDeErro,
  STATUS,
} from './scheduleUtils';
import '../styles.css';

const COR_PRINCIPAL = '#378587';

const LinhaAgendamento = ({ agendamento, onCancelar, cancelando }) => {
  const status = STATUS[agendamento.status] || {
    cor: '#5f6368',
    rotulo: agendamento.status,
  };
  return (
    <tr>
      <td style={{ padding: '8px 4px' }}>
        <EventIcon
          fontSize="small"
          style={{ verticalAlign: 'middle', marginRight: 6 }}
        />
        <strong>{agendamento.tipo_consulta}</strong>
      </td>
      <td style={{ padding: '8px 4px' }}>{agendamento.data}</td>
      <td style={{ padding: '8px 4px' }}>{agendamento.horario}</td>
      <td style={{ padding: '8px 4px' }}>
        {onCancelar ? (
          <Tooltip
            title={
              agendamento.pode_cancelar
                ? ''
                : 'O cancelamento só é permitido com 48 horas de antecedência'
            }
          >
            <span>
              <Button
                size="small"
                variant="contained"
                disabled={!agendamento.pode_cancelar || cancelando}
                onClick={() => onCancelar(agendamento)}
                style={{
                  background: agendamento.pode_cancelar ? '#c62828' : '#e6a3a3',
                  color: '#fff',
                  textTransform: 'none',
                }}
              >
                ✕ Cancelar
              </Button>
            </span>
          </Tooltip>
        ) : (
          <span
            style={{
              background: status.cor,
              color: '#fff',
              borderRadius: 4,
              padding: '4px 10px',
              fontSize: 13,
              whiteSpace: 'nowrap',
            }}
          >
            {status.rotulo}
          </span>
        )}
      </td>
    </tr>
  );
};

const TabelaAgendamentos = ({ lista, onCancelar, cancelando, vazio }) =>
  lista.length ? (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ textAlign: 'left' }}>
          <th style={{ padding: '8px 4px' }}>Agendamentos</th>
          <th style={{ padding: '8px 4px' }}>Data</th>
          <th style={{ padding: '8px 4px' }}>Horário</th>
          <th style={{ padding: '8px 4px' }}>Status</th>
        </tr>
      </thead>
      <tbody>
        {lista.map((a) => (
          <LinhaAgendamento
            key={a.id}
            agendamento={a}
            onCancelar={onCancelar}
            cancelando={cancelando}
          />
        ))}
      </tbody>
    </table>
  ) : (
    <p className="h6 text-muted mt-2">{vazio}</p>
  );

/**
 * Agendamento de consulta nas clínicas do Sindilegis (spec §6, figuras 21 a 24): o filiado
 * escolhe dia e horário livres, acompanha os agendamentos futuros e o histórico.
 */
const AppointmentSchedule = ({ match, auth }) => {
  const especialidade = match.params.especialidade;
  const info = ESPECIALIDADES[especialidade];
  const { user } = auth;

  const [painel, setPainel] = useState(null);
  const [dias, setDias] = useState({});
  const [carregandoDias, setCarregandoDias] = useState(false);
  const [dia, setDia] = useState(null);
  const [horario, setHorario] = useState(null);
  const [convenio, setConvenio] = useState('');
  const [observacao, setObservacao] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [dados, setDados] = useState({
    agendamentos: [],
    historico: [],
    bloqueios: [],
  });
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [confirmarCancelamento, setConfirmarCancelamento] = useState(null);
  const [cancelando, setCancelando] = useState(false);

  const carregarAgendamentos = useCallback(() => {
    setCarregandoLista(true);
    buscarMeusAgendamentos()
      .then(setDados)
      .catch((e) =>
        toastr.error(
          'Erro',
          mensagemDeErro(e, 'Não foi possível carregar seus agendamentos.')
        )
      )
      .finally(() => setCarregandoLista(false));
  }, []);

  useEffect(() => {
    moment.locale('pt-br');
    carregarAgendamentos();
  }, [carregarAgendamentos]);

  const abrirPainel = () => {
    setPainel(true);
    setDia(null);
    setHorario(null);
    setCarregandoDias(true);
    buscarDisponibilidade(especialidade)
      .then((r) => {
        const indice = indexarDias(r.dias);
        setDias(indice);
        const primeiro = Object.keys(indice)[0];
        if (primeiro) setDia(moment(dataDaChave(primeiro)));
      })
      .catch((e) => {
        toastr.error(
          'Erro',
          mensagemDeErro(e, 'Não foi possível carregar os horários.')
        );
        setPainel(false);
      })
      .finally(() => setCarregandoDias(false));
  };

  const diaEscolhido = dia ? dias[chaveDoDia(dia)] : null;
  const horariosDoDia = (diaEscolhido && diaEscolhido.horarios) || [];
  const futuros = useMemo(
    () => daEspecialidade(dados.agendamentos, especialidade),
    [dados, especialidade]
  );
  const historico = useMemo(
    () => daEspecialidade(dados.historico, especialidade),
    [dados, especialidade]
  );
  const bloqueio = bloqueioDaEspecialidade(dados.bloqueios, especialidade);

  const confirmar = () => {
    if (!horario) return toastr.warning('Atenção', 'Escolha um horário.');
    if (!convenio) return toastr.warning('Atenção', 'Escolha o convênio.');
    setEnviando(true);
    return agendar(especialidade, {
      agendaId: horario.agendaId,
      data: horario.data,
      indice: horario.indice,
      convenio,
      observacao,
    })
      .then((r) => {
        toastr.success(
          'Agendamento realizado',
          `Consulta marcada para ${r.data} às ${r.horario}.`
        );
        setPainel(false);
        setObservacao('');
        carregarAgendamentos();
      })
      .catch((e) => {
        toastr.error('Erro', mensagemDeErro(e, 'Não foi possível agendar.'));
        // O horário pode ter sido ocupado por outra pessoa: recarrega a disponibilidade.
        abrirPainel();
      })
      .finally(() => setEnviando(false));
  };

  const cancelarAgendamento = () => {
    const alvo = confirmarCancelamento;
    setCancelando(true);
    cancelar(especialidade, alvo.id)
      .then(() => {
        toastr.success(
          'Agendamento cancelado',
          `Consulta de ${alvo.data} às ${alvo.horario} cancelada.`
        );
        carregarAgendamentos();
      })
      .catch((e) =>
        toastr.error('Erro', mensagemDeErro(e, 'Não foi possível cancelar.'))
      )
      .finally(() => {
        setCancelando(false);
        setConfirmarCancelamento(null);
      });
  };

  if (!info) {
    return <p className="h4 mt-4">Especialidade não encontrada.</p>;
  }

  return (
    <section id="appointmentSchedule">
      <p className="body-title mt-4">
        Agendamento de consulta — {info.clinica}
      </p>
      <p className="h4 mt-2" style={{ color: '#838383' }}>
        Você pode solicitar e acompanhar seus agendamentos de consultas pelo
        Sindilegis Mais.
      </p>

      {bloqueio ? (
        <p className="h6 mt-3" style={{ color: '#c62828' }}>
          Novos agendamentos pelo Sindilegis Mais estão indisponíveis
          {bloqueio.ate
            ? ` até ${moment(bloqueio.ate).format('DD/MM/YYYY')}`
            : ''}{' '}
          por ausências sem cancelamento prévio. Entre em contato com o
          Sindilegis.
        </p>
      ) : (
        <Box mt={3}>
          <button
            type="button"
            style={{
              backgroundColor: COR_PRINCIPAL,
              padding: '12px 32px',
              color: '#fff',
            }}
            onClick={abrirPainel}
          >
            Solicitar agendamento
          </button>
        </Box>
      )}

      {painel && (
        <Box
          mt={3}
          p={2}
          style={{ border: '1px solid #e0e0e0', borderRadius: 8 }}
        >
          <p className="h5-bold">Agendamento de consulta</p>
          <Box display="flex" flexWrap="wrap" mt={1} style={{ gap: 24 }}>
            <p className="h6">Nome: {user.name}</p>
            <p className="h6">Celular: {formatPhone(user.tel)}</p>
            <p className="h6">E-mail: {user.email}</p>
          </Box>

          <p className="h6-bold mt-3">Preferência de dia</p>
          {carregandoDias ? (
            <p className="h6 text-muted">Carregando horários...</p>
          ) : !Object.keys(dias).length ? (
            <p className="h6 text-muted">
              Não há horários disponíveis no momento. Tente novamente mais
              tarde.
            </p>
          ) : (
            <Box display="flex" flexWrap="wrap" style={{ gap: 24 }}>
              <MuiPickersUtilsProvider utils={MomentUtils} locale="pt-br">
                <DatePicker
                  variant="static"
                  disableToolbar
                  value={dia}
                  onChange={(d) => {
                    setDia(d);
                    setHorario(null);
                  }}
                  shouldDisableDate={(d) => !dias[chaveDoDia(d)]}
                  disablePast
                />
              </MuiPickersUtilsProvider>
              <Box minWidth={240}>
                <p className="h6-bold">Horários disponíveis</p>
                {!horariosDoDia.length && (
                  <p className="h6 text-muted">
                    Escolha um dia disponível no calendário.
                  </p>
                )}
                {horariosDoDia.map((h) => {
                  const selecionado =
                    horario &&
                    horario.agendaId === h.agendaId &&
                    horario.indice === h.indice;
                  return (
                    <button
                      type="button"
                      key={`${h.agendaId}-${h.indice}`}
                      onClick={() => setHorario(h)}
                      aria-pressed={selecionado}
                      style={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        padding: '10px 12px',
                        marginTop: 6,
                        border: '1px solid #cfd8dc',
                        background: selecionado ? '#9ec5f8' : '#f5f5f5',
                        fontWeight: selecionado ? 600 : 400,
                      }}
                    >
                      {h.inicio} - {h.fim}
                      {h.tipo_atendimento ? ` · ${h.tipo_atendimento}` : ''}
                    </button>
                  );
                })}
              </Box>
            </Box>
          )}

          <Box display="flex" flexWrap="wrap" mt={3} style={{ gap: 16 }}>
            <FormControl variant="outlined" style={{ minWidth: 280 }}>
              <InputLabel htmlFor="agenda-convenio">
                Convênio (plano de saúde)
              </InputLabel>
              <Select
                native
                label="Convênio (plano de saúde)"
                value={convenio}
                onChange={(e) => setConvenio(e.target.value)}
                inputProps={{ id: 'agenda-convenio' }}
              >
                <option aria-label="Nenhum" value="" />
                {CONVENIOS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Select>
            </FormControl>
            <TextField
              id="agenda-observacao"
              label="Observação (opcional)"
              variant="outlined"
              value={observacao}
              onChange={(e) => setObservacao(e.target.value)}
              style={{ flex: 1, minWidth: 240 }}
            />
          </Box>

          <Box
            display="flex"
            justifyContent="flex-end"
            mt={3}
            style={{ gap: 12 }}
          >
            <button
              type="button"
              onClick={() => setPainel(false)}
              style={{ padding: '12px 24px' }}
            >
              Fechar
            </button>
            <button
              type="button"
              disabled={enviando || !horario}
              onClick={confirmar}
              style={{
                backgroundColor:
                  enviando || !horario ? '#9bbcbd' : COR_PRINCIPAL,
                padding: '12px 32px',
                color: '#fff',
              }}
            >
              {enviando ? 'Agendando...' : 'Confirmar agendamento'}
            </button>
          </Box>
        </Box>
      )}

      <Box mt={4}>
        {carregandoLista ? (
          <p className="h6 text-muted">Carregando agendamentos...</p>
        ) : (
          <TabelaAgendamentos
            lista={futuros}
            onCancelar={setConfirmarCancelamento}
            cancelando={cancelando}
            vazio="Você não tem agendamentos futuros."
          />
        )}
      </Box>

      <Accordion style={{ marginTop: 16 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          Histórico de atendimentos
        </AccordionSummary>
        <AccordionDetails style={{ display: 'block' }}>
          <TabelaAgendamentos
            lista={historico}
            vazio="Nenhum atendimento no histórico."
          />
          {historico.some((a) => a.status === 'Não compareceu') && (
            <p className="h7 mt-3" style={{ color: '#8d5524' }}>
              Ausências sem cancelamento com 48 horas de antecedência podem
              restringir novos agendamentos.
            </p>
          )}
        </AccordionDetails>
      </Accordion>

      <Dialog
        open={Boolean(confirmarCancelamento)}
        onClose={() => setConfirmarCancelamento(null)}
      >
        <DialogTitle>Cancelar agendamento?</DialogTitle>
        <DialogContent>
          {confirmarCancelamento &&
            `${confirmarCancelamento.tipo_consulta} em ${confirmarCancelamento.data} às ${confirmarCancelamento.horario}.`}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmarCancelamento(null)}>Voltar</Button>
          <Button
            onClick={cancelarAgendamento}
            disabled={cancelando}
            style={{ color: '#c62828' }}
          >
            Cancelar agendamento
          </Button>
        </DialogActions>
      </Dialog>
    </section>
  );
};

const mapStateToProps = (state) => ({ auth: state.auth });

export default connect(mapStateToProps)(AppointmentSchedule);
